import { useEffect, useState } from "react";
import { Popover, PopoverButton, PopoverPanel } from "@headlessui/react";
import { ChevronDown } from "react-bootstrap-icons";
import { useQueryClient } from "@tanstack/react-query";
import { assignPaymentProfileToCustomers } from "@api";
import { ProfilePaymentIcons } from "@components";
import { toast } from "@components/toast/Toast";
import { usePaymentProfilesContext } from "@context/PaymentProfilesContext";
import { cn } from "@utils";

const ProfileLabel = ({ profile }) => (
  <span className="inline-flex items-center gap-1.5">
    {profile.name}
    <ProfilePaymentIcons profile={profile} size="sm" />
  </span>
);

export default function PaymentProfilePicker({ customerId, customerName, paymentProfileId }) {
  const queryClient = useQueryClient();
  const { profiles } = usePaymentProfilesContext();
  const [selectedId, setSelectedId] = useState(paymentProfileId);
  const [saving, setSaving] = useState(false);

  useEffect(() => setSelectedId(paymentProfileId), [paymentProfileId]);

  const selected = profiles.find((p) => p.id === selectedId);
  const dash = <span className="text-zinc-300">—</span>;

  if (profiles.length <= 1) return selected ? <ProfileLabel profile={selected} /> : dash;

  const select = async (profile, close) => {
    close();
    if (profile.id === selectedId) return;
    const previousId = selectedId;
    setSelectedId(profile.id);
    setSaving(true);
    try {
      // The endpoint returns null on failure rather than throwing.
      const result = await assignPaymentProfileToCustomers(profile.id, [customerId]);
      if (result == null) throw new Error("Assign payment profile failed");
      toast({ description: `"${profile.name}" profile assigned to ${customerName}` });
      queryClient.invalidateQueries({ queryKey: ["customer", customerId] });
    } catch (error) {
      console.error("Failed to update payment profile:", error);
      setSelectedId(previousId);
      toast({ description: `Failed to assign "${profile.name}" profile to ${customerName}.` });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Popover className="inline-block">
      <PopoverButton
        disabled={saving}
        className="inline-flex items-center gap-1 rounded-full border border-zinc-200 bg-white px-3 py-1 text-sm font-medium text-zinc-900 hover:bg-zinc-50 focus:outline-none data-[open]:border-primary-200 data-[open]:bg-primary-50 disabled:opacity-60"
      >
        {selected ? <ProfileLabel profile={selected} /> : "Select profile"}
        <ChevronDown className="size-3.5 shrink-0 text-zinc-400" />
      </PopoverButton>
      <PopoverPanel
        anchor="bottom end"
        className="z-[60] min-w-56 overflow-hidden rounded-xl border border-zinc-200 bg-white p-1 shadow-lg [--anchor-gap:0.4rem]"
      >
        {({ close }) =>
          profiles.map((profile) => (
            <button
              key={profile.id}
              type="button"
              onClick={() => select(profile, close)}
              className={cn(
                "flex w-full items-center rounded-lg px-3 py-2 text-left text-sm font-medium hover:bg-zinc-50",
                profile.id === selectedId ? "text-primary-500" : "text-zinc-900",
              )}
            >
              <ProfileLabel profile={profile} />
            </button>
          ))
        }
      </PopoverPanel>
    </Popover>
  );
}
